import React, { useEffect, useState } from "react";
import { Megaphone, Copy, TrendingUp, Users, Wallet, BadgeCheck, Clock } from "lucide-react";
import { useI18n } from "../../contexts/I18nContext";
import { toast } from "sonner";
import api from "../../lib/api";

/**
 * Tile shown on /dashboard for users who applied as influencers.
 * Displays:
 *  - Status (pending / approved / rejected)
 *  - Their referral code (if approved)
 *  - Total earnings, paid, signups
 *  - Shareable referral link
 *
 * Hidden completely if the user has never applied (status === "none").
 */
export default function InfluencerStatusCard() {
  const { lang } = useI18n();
  const ar = lang === "ar";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    api
      .get("/influencers/me")
      .then((r) => {
        if (!cancelled) setData(r.data);
      })
      .catch(() => {
        if (!cancelled) setData({ status: "none" });
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading || !data || data.status === "none") return null;

  const status = data.status;
  const code = data.code;
  const link = code ? `https://thaqh.com/?ref=${code}` : "";

  const copy = (txt, label) => {
    navigator.clipboard.writeText(txt);
    toast.success(ar ? `تم نسخ ${label}` : `${label} copied`);
  };

  return (
    <section
      className="rounded-2xl border border-[#D4AF37]/30 p-4 bg-gradient-to-br from-[#D4AF37]/10 to-black/60"
      data-testid="influencer-status-card"
    >
      <header className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-black gold-text inline-flex items-center gap-2">
          <Megaphone className="h-4 w-4" />
          {ar ? "حسابي كمؤثر" : "My Influencer Profile"}
        </h3>
        <StatusBadge status={status} ar={ar} />
      </header>

      {status === "approved" && (
        <>
          {/* Referral code */}
          <div className="rounded-xl bg-black/60 border border-[#D4AF37]/40 p-3 mb-3">
            <div className="text-[10px] text-white/60 mb-1">{ar ? "كود الإحالة" : "Referral code"}</div>
            <div className="flex items-center justify-between gap-2">
              <span className="font-mono text-lg font-black gold-text" data-testid="inf-code">
                {code}
              </span>
              <button
                onClick={() => copy(code, ar ? "الكود" : "Code")}
                className="h-8 px-3 rounded-lg bg-[#D4AF37]/15 border border-[#D4AF37]/40 text-[#D4AF37] text-xs inline-flex items-center gap-1"
                data-testid="inf-copy-code"
              >
                <Copy className="h-3 w-3" />
                {ar ? "نسخ" : "Copy"}
              </button>
            </div>
          </div>

          {/* Share link */}
          <div className="rounded-xl bg-black/40 border border-white/10 p-3 mb-3">
            <div className="text-[10px] text-white/60 mb-1">{ar ? "رابط الإحالة" : "Share link"}</div>
            <div className="flex items-center justify-between gap-2">
              <span className="font-mono text-[11px] text-white/85 truncate flex-1">{link}</span>
              <button
                onClick={() => copy(link, ar ? "الرابط" : "Link")}
                className="h-7 px-2 rounded-md bg-white/5 border border-white/10 text-white/80 text-[10px] inline-flex items-center gap-1 shrink-0"
                data-testid="inf-copy-link"
              >
                <Copy className="h-3 w-3" />
                {ar ? "نسخ" : "Copy"}
              </button>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-2">
            <Stat icon={Users} label={ar ? "إحالات" : "Signups"} value={data.signups || 0} />
            <Stat
              icon={TrendingUp}
              label={ar ? "أرباح" : "Earnings"}
              value={`${(data.earnings_total || 0).toFixed(0)}`}
              suffix={ar ? "ر.س" : "SAR"}
            />
            <Stat
              icon={Wallet}
              label={ar ? "مدفوع" : "Paid"}
              value={`${(data.earnings_paid || 0).toFixed(0)}`}
              suffix={ar ? "ر.س" : "SAR"}
            />
          </div>

          <p className="text-[10px] text-white/55 mt-3 text-center">
            {ar
              ? "كل اشتراك بكودك = 19.80 ر.س لك. التحويل شهرياً بعد 100 ر.س."
              : "Each signup with your code = 19.80 SAR. Monthly payout after 100 SAR."}
          </p>

          {data.recent_signups?.length > 0 ? (
            <div className="mt-3 rounded-xl bg-black/40 border border-white/10 p-3" data-testid="inf-recent-signups">
              <div className="text-[10px] text-white/55 uppercase tracking-wider mb-2">
                {ar ? "آخر الإحالات" : "Recent signups"}
              </div>
              <ul className="space-y-1">
                {data.recent_signups.slice(0, 5).map((s, i) => (
                  <li key={s.id || `s-${i}`} className="flex items-center justify-between text-[11px]">
                    <span className="text-white/70 truncate">
                      {(s.created_at || "").slice(0, 10)}
                    </span>
                    <span className="font-bold text-[#D4AF37]">
                      +{(s.commission || 0).toFixed(2)} {ar ? "ر.س" : "SAR"}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ) : (data.signups || 0) > 0 ? (
            <div className="mt-3 rounded-xl bg-black/40 border border-white/10 p-3 text-center" data-testid="inf-recent-signups-empty">
              <div className="text-[10px] text-white/55 uppercase tracking-wider mb-1">
                {ar ? "آخر الإحالات" : "Recent signups"}
              </div>
              <p className="text-[11px] text-white/50">
                {ar ? "تفاصيل الإحالات الأخيرة ستظهر هنا قريباً." : "Detailed signup history will appear here soon."}
              </p>
            </div>
          ) : null}
        </>
      )}

      {status === "pending" && (
        <p className="text-xs text-white/75 text-center py-3">
          {ar ? "طلبك قيد المراجعة. سنخبرك خلال 48 ساعة." : "Application under review. We'll notify you within 48h."}
        </p>
      )}

      {status === "rejected" && (
        <div className="text-center py-3">
          <p className="text-xs text-red-300/85">{ar ? "تم رفض طلبك." : "Your application was rejected."}</p>
          {data.rejection_reason && (
            <p className="text-[10px] text-white/55 mt-1">{data.rejection_reason}</p>
          )}
        </div>
      )}
    </section>
  );
}

function Stat({ icon: Icon, label, value, suffix }) {
  return (
    <div className="rounded-lg bg-black/40 border border-white/10 p-2 text-center">
      <Icon className="h-4 w-4 text-[#D4AF37] mx-auto mb-1" />
      <div className="text-sm font-black text-white" data-testid={`inf-stat-${label}`}>
        {value}
        {suffix && <span className="text-[9px] text-white/55 ms-0.5">{suffix}</span>}
      </div>
      <div className="text-[9px] text-white/55">{label}</div>
    </div>
  );
}

function StatusBadge({ status, ar }) {
  const map = {
    approved: {
      icon: BadgeCheck,
      bg: "bg-emerald-400/15 border-emerald-400/40 text-emerald-300",
      label: ar ? "نشط" : "Active",
    },
    pending: {
      icon: Clock,
      bg: "bg-amber-400/15 border-amber-400/40 text-amber-300",
      label: ar ? "قيد المراجعة" : "Pending",
    },
    rejected: {
      icon: Clock,
      bg: "bg-rose-400/15 border-rose-400/40 text-rose-300",
      label: ar ? "مرفوض" : "Rejected",
    },
  };
  const cfg = map[status];
  if (!cfg) return null;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full border text-[10px] font-bold ${cfg.bg}`}>
      <Icon className="h-3 w-3" />
      {cfg.label}
    </span>
  );
}
